// Turning what was typed into a /search field into the ilike filters sent to
// PostgREST — the one place that decides what "contains" means per column.
//
// Patient names are stored as one string, conventionally "Last, First"
// ("Rogers, Pamela"), though "First Last" occurs too (see patientNameParts.js).
// A single `ilike '%Pamela Rogers%'` matches only the spelling that happens to
// agree with the stored order, so a name typed the other way round found
// nothing and read as "no such patient". Name columns are therefore matched
// word by word: one ilike per word, which PostgREST ANDs, so every word typed
// must appear somewhere in the stored name — in any order.
//
// Every other column keeps the single substring match. An email or a chart
// number is one token, and splitting "ted smith@x.com" would match far more
// than what was typed.

/** Columns holding a person's name, matched word by word. */
export const NAME_SEARCH_COLUMNS = ['patient_name', 'physician_name'];

// Words are separated by whitespace, the comma of "Last, First", or the period
// after an initial. Apostrophes and hyphens stay inside a word — "O'Brien" and
// "Smith-Jones" are typed the way they are stored.
const WORD_SEPARATORS = /[\s,.]+/;

/** The words of a typed name, in the order typed. Empty for a blank value. */
export function nameSearchTerms(value) {
  return String(value ?? '').split(WORD_SEPARATORS).filter(Boolean);
}

/** The substrings `column` must contain for a row to match `value`. */
export function containsFilterTerms(column, value) {
  const text = String(value ?? '').trim();
  if (!text) return [];
  return NAME_SEARCH_COLUMNS.includes(column) ? nameSearchTerms(text) : [text];
}

/**
 * Add the ilike filter(s) for one search field to a PostgREST query builder
 * and return the builder (supabase-js builders chain). A blank value adds
 * nothing, so callers need not check first.
 */
export function applyContainsFilters(query, column, value) {
  if (DATE_SEARCH_COLUMNS.includes(column)) return applyDateFilter(query, column, value);
  return containsFilterTerms(column, value).reduce(
    (q, term) => q.ilike(column, `%${term}%`),
    query,
  );
}

// ---------------------------------------------------------------------------
// Date columns (`dob` on both patient tables) are real Postgres dates, and
// `ilike` against a date raises `operator does not exist: date ~~* unknown` —
// which the per-schema try/catch in /search swallowed, so ANY value in the DOB
// box came back as "No results". Dates are matched as dates instead: a full
// date with `eq`, a month or a year as a `gte`/`lte` range, whatever format
// was typed.
// ---------------------------------------------------------------------------

/** Columns holding a date, matched by value/range rather than substring. */
export const DATE_SEARCH_COLUMNS = ['dob'];

const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9,
  september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

const pad2 = (n) => String(n).padStart(2, '0');
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const validDay = (y, m, d) => m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);

// "26" -> 2026, "27" -> 1927: two-digit years pivot on the current year, since
// a DOB of '27 is far more likely 1927 than 2027.
const fullYear = (y) => {
  if (y.length === 4) return Number(y);
  const n = Number(y);
  const pivot = new Date().getFullYear() % 100;
  return n <= pivot ? 2000 + n : 1900 + n;
};

const day = (y, m, d) =>
  validDay(y, m, d) ? { kind: 'day', from: `${y}-${pad2(m)}-${pad2(d)}`, to: `${y}-${pad2(m)}-${pad2(d)}` } : null;
const month = (y, m) =>
  m >= 1 && m <= 12 ? { kind: 'month', from: `${y}-${pad2(m)}-01`, to: `${y}-${pad2(m)}-${pad2(daysInMonth(y, m))}` } : null;
const year = (y) => (y >= 1000 && y <= 9999 ? { kind: 'year', from: `${y}-01-01`, to: `${y}-12-31` } : null);

/**
 * Read a date typed in any common format.
 *
 *   2026-09-01 · 2026/9/1 · 20260901 · 9/1/2026 · 09-01-2026 · 9.1.26
 *   sept 1 2026 · Sep 1, 2026 · September 1st 2026 · 1 sep 2026 · 2026 sep 1
 *   partial: 2026 · sep 2026 · 9/2026 · 2026-09
 *
 * Numeric a/b/c is read US-style, month first; when the first number cannot
 * be a month (13/9/2026) it is read day first. A month-and-day with no year
 * cannot be turned into a range, so it is not a date here.
 *
 * @returns {{kind:'day'|'month'|'year', from:string, to:string}|null}
 */
export function parseSearchDate(value) {
  const text = String(value ?? '').trim().toLowerCase().replace(/(\d)(st|nd|rd|th)\b/g, '$1').replace(/,/g, ' ');
  if (!text) return null;
  let m;

  // ISO-first: 2026-09-01, 2026/9/1, 2026.09.01, 2026-09, 20260901
  if ((m = text.match(/^(\d{4})[-/. ](\d{1,2})(?:[-/. ](\d{1,2}))?$/))) {
    return m[3] ? day(Number(m[1]), Number(m[2]), Number(m[3])) : month(Number(m[1]), Number(m[2]));
  }
  if ((m = text.match(/^(\d{4})(\d{2})(\d{2})$/))) return day(Number(m[1]), Number(m[2]), Number(m[3]));
  if ((m = text.match(/^(\d{4})$/))) return year(Number(m[1]));

  // Numeric, month first: 9/1/2026, 09-01-26, 9.1.2026, 9/2026
  if ((m = text.match(/^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{2}|\d{4})$/))) {
    const [a, b, y] = [Number(m[1]), Number(m[2]), fullYear(m[3])];
    return a > 12 && b <= 12 ? day(y, b, a) : day(y, a, b);
  }
  if ((m = text.match(/^(\d{1,2})[-/. ](\d{4})$/))) return month(Number(m[2]), Number(m[1]));

  // Month names in any position: sept 1 2026 · 1 sep 2026 · 2026 sep 1 · sep 2026
  if ((m = text.match(/^(?:(\d{1,4})[\s.]+)?([a-z]{3,9})\.?(?:[\s.]+(\d{1,4}))?(?:[\s.]+(\d{1,4}))?$/))) {
    const mon = MONTHS[m[2]];
    if (!mon) return null;
    const nums = [m[1], m[3], m[4]].filter(Boolean);
    if (nums.length === 0) return null;
    const yearTok = nums.find((n) => n.length === 4) || (nums.length === 2 ? nums[1] : nums[0]);
    const dayTok = nums.find((n) => n !== yearTok);
    const y = fullYear(yearTok);
    if (!dayTok) return nums.length === 1 && yearTok.length <= 2 ? null : month(y, mon);
    return day(y, mon, Number(dayTok));
  }
  return null;
}

/** True when the value belongs to a date column but cannot be read as a date. */
export function isUnreadableDate(column, value) {
  return DATE_SEARCH_COLUMNS.includes(column) && String(value ?? '').trim() !== '' && !parseSearchDate(value);
}

/**
 * The filter a date column gets: `eq` for a full date, a `gte`/`lte` range for
 * a month or a year. An unreadable value must never reach PostgREST as an
 * ilike; callers check `isUnreadableDate` first and tell the user instead.
 */
export function applyDateFilter(query, column, value) {
  const parsed = parseSearchDate(value);
  if (!parsed) return query;
  if (parsed.kind === 'day') return query.eq(column, parsed.from);
  return query.gte(column, parsed.from).lte(column, parsed.to);
}
